/**
 * Iter 118.1 — Auth-return preservation helpers.
 *
 * Two mechanisms working together:
 *
 *   1. Query param ``?next=<encoded>`` — primary, survives normal
 *      login flows and is the source of truth when present.
 *   2. localStorage ``pendingRedirect`` — fallback for OAuth / PWA
 *      session weirdness where the query string can be dropped
 *      (e.g. Google OAuth redirect chain).
 *
 * ``next`` MUST always be a same-origin relative path — external URLs
 * are rejected client-side before ever hitting the login screen. The
 * backend ``/api/go`` layer enforces the same check server-side, so
 * we have defense in depth.
 */

const STORAGE_KEY = 'gr8ful.pendingRedirect';
const LOGOUT_FLAG_KEY = 'gr8ful.justLoggedOutAt';
const LOGOUT_FLAG_WINDOW_MS = 5000;
const MAX_LEN = 512;

/** Called by the logout handler right before clearing the session.
 *  Explicitly logging out and hitting a protected route mid-session
 *  (session expired, or a deep link while logged out) look identical
 *  to RequireAuthRedirect -- both are "currentUser is null on a
 *  protected route" -- but they mean opposite things for where login
 *  should return to. Logging out from Profile (where the button
 *  usually lives) isn't "I was trying to reach Profile"; this lets
 *  captureAndBuildLoginUrl tell the two apart for the redirect(s) that
 *  immediately follow, without an involuntary bounce minutes later
 *  reusing a stale flag.
 *
 *  A timestamp + time window, not a read-once flag: RequireAuthRedirect
 *  calls captureAndBuildLoginUrl directly in its render body (not a
 *  useEffect), and React.StrictMode deliberately double-invokes render
 *  bodies in dev to catch exactly this kind of non-idempotent side
 *  effect -- a read-once "consume" flag passes the first invocation and
 *  silently fails the second, since the first already cleared it. A
 *  peek-only time check is safe to call any number of times. */
export function markVoluntaryLogout() {
  try { sessionStorage.setItem(LOGOUT_FLAG_KEY, String(Date.now())); } catch (_) {}
}

function wasVoluntaryLogoutJustNow() {
  try {
    const stamped = Number(sessionStorage.getItem(LOGOUT_FLAG_KEY));
    return Boolean(stamped) && Date.now() - stamped < LOGOUT_FLAG_WINDOW_MS;
  } catch (_) {
    return false;
  }
}

/** Same-origin path validator — mirror of the backend
 *  ``is_safe_relative_path``. Rejects: absolute URLs, protocol-
 *  relative URLs, non-slash-leading strings, and dangerous schemes. */
export function isSafeRelativePath(path) {
  if (!path || typeof path !== 'string') return false;
  if (path.length > MAX_LEN) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  // Reject any scheme prefix (http:, javascript:, data:, etc).
  if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(path)) return false;
  return true;
}

/** Capture the current in-app location as the return-target and
 *  return a login URL with ``?next=`` set. Also persists a
 *  ``pendingRedirect`` fallback so OAuth flows that drop query
 *  params can still return home. */
export function captureAndBuildLoginUrl(location) {
  if (wasVoluntaryLogoutJustNow()) return '/';
  const target = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  if (isSafeRelativePath(target)) {
    try { localStorage.setItem(STORAGE_KEY, target); } catch (_) {}
    return `/?next=${encodeURIComponent(target)}`;
  }
  return '/';
}

/** Called by the login/signup success handler to consume the next
 *  target and navigate. Returns the resolved target path or null. */
export function consumePendingRedirect(searchParams) {
  // 1) ``?next=`` wins if present + safe.
  const nextFromQuery = searchParams?.get?.('next');
  if (nextFromQuery && isSafeRelativePath(nextFromQuery)) {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    return nextFromQuery;
  }
  // 2) localStorage fallback (used by OAuth chains).
  let stored = null;
  try { stored = localStorage.getItem(STORAGE_KEY); } catch (_) {}
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  if (stored && isSafeRelativePath(stored)) return stored;
  return null;
}

/** Persist a pending redirect explicitly. Used by ``/go`` before it
 *  navigates a logged-out user to the login page. */
export function stashPendingRedirect(target) {
  if (!isSafeRelativePath(target)) return;
  try { localStorage.setItem(STORAGE_KEY, target); } catch (_) {}
}

/** Clear without consuming — used when the pending target becomes
 *  unreachable (e.g. after unauthorized landing). */
export function clearPendingRedirect() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}
